// Verzamelt client-side errors en stuurt ze naar onze eigen `/forms/client-error`
// endpoint, dat ze doorstuurt naar Sentry. Zo hebben we geen aparte Sentry
// browser-SDK nodig.
//
// Wat wel werkt:
//   - Uncaught exceptions (window 'error')
//   - Unhandled promise-rejections
//   - React rendering errors (via ErrorBoundary, die `reportClientError`
//     aanroept met componentStack)
//   - Handmatige `reportClientError(err, { extra })` calls
//
// Wat NIET werkt (acceptable trade-off):
//   - Source-map mapping (server moet 'm doen of we lezen de gemangelde stack)
//   - Sessie-replay
//   - Mouse/console breadcrumbs

const apiBase = process.env.REACT_APP_API_BASE_URL ?? "";

const enabled = !!apiBase;
const sourceTag = "dashboard";

// Dedupe: dezelfde error-message twee keer per minuut blokken zodat een
// render-loop niet de rate-limit triggert. Niet bullet-proof maar genoeg.
const recentErrors = new Map(); // key -> timestamp
const DEDUPE_WINDOW_MS = 60_000;

const shouldSend = (key) => {
  if (!key) return true;
  const last = recentErrors.get(key);
  const now = Date.now();
  if (last && now - last < DEDUPE_WINDOW_MS) return false;
  recentErrors.set(key, now);
  // garbage-collect oudere entries
  if (recentErrors.size > 50) {
    for (const [k, t] of recentErrors) {
      if (now - t > DEDUPE_WINDOW_MS) recentErrors.delete(k);
    }
  }
  return true;
};

export const reportClientError = (error, extra = {}) => {
  if (!enabled) return;
  try {
    const message = error?.message ?? String(error ?? "Onbekende fout");
    const stack = error?.stack ?? "";
    const dedupeKey = `${message}::${stack.slice(0, 200)}`;
    if (!shouldSend(dedupeKey)) return;

    const payload = {
      source: sourceTag,
      message,
      stack,
      componentStack: extra.componentStack ?? "",
      filename: extra.filename ?? "",
      lineno: extra.lineno ?? null,
      colno: extra.colno ?? null,
      url: typeof window !== "undefined" ? window.location.href : "",
      route: typeof window !== "undefined" ? window.location.pathname : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      release: process.env.REACT_APP_RELEASE ?? "",
    };
    // Fire-and-forget. Geen await want een page-error mag niet wachten op
    // de network round-trip.
    fetch(`${apiBase}/forms/client-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Bewust niets - als de reporter zelf faalt, willen we geen oneindige
      // loop triggeren door dat ook te rapporteren.
    });
  } catch {
    // Reporter mag NOOIT zelf een uitzondering throwen.
  }
};

// Registreer de window-level handlers één keer bij app-boot. Idempotent.
let initialised = false;
export const initClientErrorReporter = () => {
  if (initialised) return;
  initialised = true;
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    const err = event.error ?? new Error(event.message || "Onbekende fout");
    reportClientError(err, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const err =
      reason instanceof Error
        ? reason
        : new Error(typeof reason === "string" ? reason : "Unhandled rejection");
    reportClientError(err);
  });
};
